import type {
  AccountDeletion,
  ApproveRequestRequest,
  DeleteAccountRequest,
  UpdateMeRequest,
  AuthProviders,
  ChangePasswordRequest,
  ChoosePasswordRequest,
  CreateInvitationRequest,
  CreateTenantRequest,
  DeleteTenantRequest,
  EchoRequest,
  EchoResponse,
  Feedback,
  FeedbackList,
  FeedbackRequest,
  FeedbackUpdate,
  HealthCheck,
  HiddenResults,
  InvitationList,
  InvitationTokenRequest,
  LeaveTenantRequest,
  Me,
  MemberList,
  MembershipRequestList,
  PasswordLink,
  PendingInvitationList,
  SentRequestList,
  SpacePreview,
  Profile,
  RevokeInvitationRequest,
  SignInRequest,
  SignUpRequest,
  SignUpResponse,
  Space,
  Tenant,
  TenantDeletion,
  TenantLeaving,
  TenantList,
  TenantSearchList,
  UpdateGrantsRequest,
  UpdateMemberRequest,
  UpdateProfileRequest,
  UpdateTenantRequest,
  UserList,
  CreateEventRequest,
  CreatePartRequest,
  EventOptions,
  EventPage,
  EventFollow,
  FollowEventRequest,
  HomeFeedPage,
  SpaceEvent,
  UpdateEventRequest,
  UpdatePartRequest,
  Entry,
  EntryPage,
  Leaderboard,
  VerificationQueue,
  VerifyRequest,
  Overall,
  RecordResultsRequest,
  UpdateEntryRequest,
  LogWorkoutRequest,
  InboxPage,
  InboxSummary,
  MarkInboxReadRequest,
  WaitingPage,
  CommentPage,
  EditCommentRequest,
  ReactionSummary,
  ReactorPage,
  SharedResult,
  SpaceComment,
  WriteCommentRequest,
  AcceptTeamRequest,
  AddGuestsRequest,
  GuestRequest,
  InviteToTeamRequest,
  Team,
  TeamList,
  TeamNoteRequest,
  TeamResultRequest,
  Picture,
  UploadForm,
} from "@repo/shared-types";
import type { RequestBody } from "@repo/shared-types";
import type { ApiClient } from "./client";

/**
 * Paths here are written WITHOUT the `/api` prefix. Where the api sits under an
 * origin is deployment topology, so the prefix belongs to the base URL the
 * caller builds, not to an endpoint's identity.
 */
export const createApi = (client: ApiClient) => ({
  health: {
    get: () => client.get<HealthCheck>("/health"),
  },
  me: {
    get: (init?: RequestInit) => client.get<Me>("/me", init),
    /**
     * Changes what the caller can change about themselves. Call
     * invalidateSession() afterwards: the username is on the session shape.
     */
    update: (body: RequestBody<UpdateMeRequest>) => client.patch<Me>("/me", body),
    /**
     * The caller's private defaults — division, units. Nobody else can read
     * them. `update` changes only the fields sent: leave one out to keep it,
     * send "" to clear it.
     */
    profile: {
      get: () => client.get<Profile>("/me/profile"),
      update: (body: RequestBody<UpdateProfileRequest>) =>
        client.patch<Profile>("/me/profile", body),
    },
    /**
     * The caller's profile picture (7g). `start` answers a form to post one
     * square JPEG straight to the store, then confirm with
     * `space("me").media.uploaded`; `remove` takes it down, back to
     * initials. Call invalidateSession() after either: the image is on the
     * session shape.
     */
    avatar: {
      start: () => client.post<UploadForm>("/me/avatar"),
      remove: () => client.delete<void>("/me/avatar"),
    },
    /**
     * Changes the password, given the current one. Every other session ends;
     * this one carries on. An account with no password sets one through
     * `auth.forgotPassword` instead.
     */
    changePassword: (body: ChangePasswordRequest) => client.post<void>("/me/password", body),
    /** What would stop this account being deleted. Empty means nothing does. */
    deletion: () => client.get<AccountDeletion>("/me/deletion"),
    /** Irreversible. `confirm` must be the caller's own username. */
    delete: (body: RequestBody<DeleteAccountRequest>) =>
      client.post<void>("/me/deletion", body),
  },
  auth: {
    /** Which social providers this deployment has credentials for. */
    providers: () => client.get<AuthProviders>("/auth/providers"),
    /**
     * Asks for an account: the address alone. Nothing exists until the
     * emailed link's page chooses the password — or, where no mail is sent,
     * until `token` comes back and the client opens that page itself. Asking
     * again is how a link is sent again.
     */
    signUp: (body: RequestBody<SignUpRequest>) =>
      client.post<SignUpResponse>("/auth/sign-up", body),
    signIn: (body: SignInRequest) => client.post<Me>("/auth/sign-in", body),
    signOut: () => client.post<void>("/auth/sign-out"),
    /** Deprecated: `signUp` again does the same. */
    resendVerification: (email: string) =>
      client.post<void>("/auth/verify-email/resend", { email }),
    /**
     * What a sign-up or reset link is for, read without spending it. The
     * token comes from the page's fragment and travels in the body, so no
     * server ever logs it.
     */
    passwordLink: (token: string) => client.post<PasswordLink>("/auth/password/link", { token }),
    /** Spends the link with the password chosen, and signs in. */
    choosePassword: (body: RequestBody<ChoosePasswordRequest>) =>
      client.post<Me>("/auth/password", body),
    /** Emails a reset link, or a first password's; answers nothing either way. */
    forgotPassword: (email: string) => client.post<void>("/auth/password/forgot", { email }),
    /**
     * Where to send the browser to start a provider sign-in.
     *
     * A full-page navigation, never a fetch: the flow leaves the origin, and
     * the session cookie is set on a redirect back. `redirect` must be a path
     * within the app — the api refuses anything else, so this cannot become an
     * open redirect.
     */
    oauthUrl: (provider: "google", redirect = "/") =>
      `/api/auth/oauth/${provider}?redirect=${encodeURIComponent(redirect)}`,
  },
  echo: {
    post: (body: EchoRequest) => client.post<EchoResponse>("/echo", body),
  },

  /**
   * Spaces the caller belongs to, and the directory of public ones.
   *
   * Everything here is about the person asking. Anything about ONE
   * space lives under `space` below, which mirrors how the api routes
   * them: these are on the protected mux, those are behind the membership
   * middleware.
   */
  tenants: {
    list: () => client.get<TenantList>("/tenants"),
    create: (body: RequestBody<CreateTenantRequest>) => client.post<Tenant>("/tenants", body),
    /**
     * The directory: public spaces, plus any you already belong to,
     * each carrying where you stand with it so a caller can label the row
     * rather than guess. One page of 20: pass `nextCursor` back, with the
     * same query, for the next.
     */
    search: (query: string, cursor?: string | null) => {
      const params = new URLSearchParams({ q: query });
      if (cursor) params.set("cursor", cursor);
      return client.get<TenantSearchList>(`/tenants/search?${params}`);
    },
    /**
     * @deprecated Points the session at another space, which only
     * `Me.activeTenant` reflects; the web app shows no current space. Every
     * space route names its space in its path instead.
     */
    switch: (slug: string) => client.post<Tenant>(`/tenants/${encodeURIComponent(slug)}/switch`),
    /**
     * A public space as somebody outside it reads it: its presentation page.
     * 404 for a private, personal or missing one, alike.
     */
    preview: (slug: string) => client.get<SpacePreview>(`/tenants/${encodeURIComponent(slug)}/preview`),
    requestToJoin: (slug: string) =>
      client.post<void>(`/tenants/${encodeURIComponent(slug)}/request`),
    /**
     * Takes back the caller's waiting request to join. 404 when there is none
     * — and the same for a space that is private or missing.
     */
    withdrawRequest: (slug: string) =>
      client.delete<void>(`/tenants/${encodeURIComponent(slug)}/request`),
  },

  /** The caller's requests to join that nobody has answered yet, newest first. */
  requests: {
    list: () => client.get<SentRequestList>("/requests"),
  },

  users: {
    search: (query: string) =>
      client.get<UserList>(`/users/search?q=${encodeURIComponent(query)}`),
  },

  /**
   * What an event can be described with, and the ids to send: sports,
   * formats and score types, each a list the api keeps, and for a score type
   * what it measures and whether its part takes a time limit. The same for
   * every space.
   */
  eventOptions: () => client.get<EventOptions>("/event-options"),

  /**
   * The caller's log: every result they have, private and shared, newest by
   * when it was done. Entries live in the personal space, so these paths are
   * under /t/me, and they work after leaving the space an entry came from.
   */
  entries: {
    /** One page. Pass `nextCursor` back for the next. */
    list: (cursor?: string | null) =>
      client.get<EntryPage>(cursor ? `/t/me/entries?cursor=${encodeURIComponent(cursor)}` : "/t/me/entries"),
    get: (id: string) => client.get<Entry>(`/t/me/entries/${encodeURIComponent(id)}`),
    /**
     * Logs a workout of your own, with no event behind it: what it was and
     * how it went, in one call. An `id` of your own makes a retry answer the
     * workout it logged.
     */
    log: (body: RequestBody<LogWorkoutRequest>) => client.post<Entry>("/t/me/entries", body),
    /** Parts are named by the entry's own part ids; each one sent is replaced. */
    update: (id: string, body: RequestBody<UpdateEntryRequest>) =>
      client.patch<Entry>(`/t/me/entries/${encodeURIComponent(id)}`, body),
    delete: (id: string) => client.delete<void>(`/t/me/entries/${encodeURIComponent(id)}`),
    /** Takes it off the leaderboards, wherever it is shared. */
    unshare: (id: string) => client.delete<void>(`/t/me/entries/${encodeURIComponent(id)}/share`),
    /**
     * The workout's photo, from the log (7g): a form to post it with, then
     * `space("me").media.uploaded`; taking it down keeps its 24 hours as
     * they were.
     */
    photo: {
      start: (id: string) => client.post<UploadForm>(`/t/me/entries/${encodeURIComponent(id)}/photo`),
      remove: (id: string) => client.delete<void>(`/t/me/entries/${encodeURIComponent(id)}/photo`),
    },
    /**
     * The thread on one of your shared results, read from the log while you
     * are still a member of its space. Nothing is written from here: the
     * result's page in its space is where to reply.
     */
    comments: (id: string, cursor?: string | null) =>
      client.get<CommentPage>(withCursor(`/t/me/entries/${encodeURIComponent(id)}/comments`, cursor)),
    replies: (id: string, commentId: string, cursor?: string | null) =>
      client.get<CommentPage>(
        withCursor(
          `/t/me/entries/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}/replies`,
          cursor,
        ),
      ),
    /**
     * A team you are on, from the log: its thread, read while its score is
     * on its board and you are still in its space — replying is on the
     * team's page — and your own note on it, written from here too.
     */
    teams: {
      comments: (teamId: string, cursor?: string | null) =>
        client.get<CommentPage>(withCursor(`/t/me/teams/${encodeURIComponent(teamId)}/comments`, cursor)),
      replies: (teamId: string, commentId: string, cursor?: string | null) =>
        client.get<CommentPage>(
          withCursor(
            `/t/me/teams/${encodeURIComponent(teamId)}/comments/${encodeURIComponent(commentId)}/replies`,
            cursor,
          ),
        ),
      note: (teamId: string, body: RequestBody<TeamNoteRequest>) =>
        client.put<void>(`/t/me/teams/${encodeURIComponent(teamId)}/note`, body),
    },
  },

  /**
   * Invitations addressed to the caller. Accepting is always explicit.
   *
   * The handle goes in the body, never the path: every request is logged with
   * its path, and an invitation token is a credential. `token` is what an
   * emailed link carries; `id` is what the list carries, since the token is
   * hashed on creation and cannot be echoed back to a page.
   */
  invitations: {
    list: () => client.get<PendingInvitationList>("/invitations"),
    accept: (handle: RequestBody<InvitationTokenRequest>) =>
      client.post<Tenant>("/invitations/accept", handle),
    decline: (handle: RequestBody<InvitationTokenRequest>) =>
      client.post<void>("/invitations/decline", handle),
  },

  /**
   * The home feed: what every space the caller is in has published, the
   * latest published first, each event with its space. `"following"` is
   * Home's other view: the events the caller follows, the last followed
   * first. Pass `nextCursor` back, with the same view, for the next page.
   */
  feed: (cursor?: string | null, view: "all" | "following" = "all") => {
    const query = new URLSearchParams();
    if (view !== "all") query.set("view", view);
    if (cursor) query.set("cursor", cursor);
    return client.get<HomeFeedPage>(query.size > 0 ? `/feed?${query}` : "/feed");
  },

  /**
   * The feedback board (0091), offered only while `Me.feedback`; every route
   * is a 404 otherwise. `send` stores it and emails it to the people building
   * the app — 1 to 5,000 characters, five a day per account (429 past that) —
   * and answers it, 201. `list` is the board, newest first, filtered by kind
   * and status; pass `nextCursor` back with the same filters. `update` and
   * removing somebody else's are the runner's (`Me.runsFeedback`); anybody
   * else is answered 404. `back` is "me too", never on your own (409).
   * `picture.start` answers an upload form for its sender alone, confirmed
   * with `space("me").media.uploaded`.
   */
  feedback: {
    send: (body: RequestBody<FeedbackRequest>) => client.post<Feedback>("/feedback", body),
    list: (filters: FeedbackFilters = {}, cursor?: string | null) => {
      const query = new URLSearchParams();
      for (const kind of filters.kind ?? []) query.append("kind", kind);
      for (const status of filters.status ?? []) query.append("status", status);
      if (cursor) query.set("cursor", cursor);
      return client.get<FeedbackList>(query.size > 0 ? `/feedback?${query}` : "/feedback");
    },
    get: (id: string) => client.get<Feedback>(`/feedback/${encodeURIComponent(id)}`),
    update: (id: string, body: RequestBody<FeedbackUpdate>) =>
      client.patch<Feedback>(`/feedback/${encodeURIComponent(id)}`, body),
    remove: (id: string) => client.delete<void>(`/feedback/${encodeURIComponent(id)}`),
    back: (id: string) => client.put<Feedback>(`/feedback/${encodeURIComponent(id)}/backing`),
    unback: (id: string) => client.delete<Feedback>(`/feedback/${encodeURIComponent(id)}/backing`),
    picture: {
      start: (id: string) => client.post<UploadForm>(`/feedback/${encodeURIComponent(id)}/picture`),
    },
  },

  /**
   * The caller's notifications across every space, newest first. The unread
   * count rides on `me` instead, so the header needs no request of its own.
   */
  inbox: {
    list: (cursor?: string | null) =>
      client.get<InboxPage>(cursor ? `/inbox?cursor=${encodeURIComponent(cursor)}` : "/inbox"),
    /**
     * Reads everything up to the newest line shown — send its `createdAt`
     * exactly as the inbox gave it — so one written since stays unread. Call
     * invalidateSession() afterwards: the count is on the session shape.
     */
    read: (body: RequestBody<MarkInboxReadRequest>) => client.post<InboxSummary>("/inbox/read", body),
    /** For the spaces where the caller verifies, each event with results waiting. */
    waiting: (cursor?: string | null) =>
      client.get<WaitingPage>(
        cursor ? `/inbox/waiting?cursor=${encodeURIComponent(cursor)}` : "/inbox/waiting",
      ),
  },

  /**
   * One space, by slug. Every path is under /t/{slug}/, which is the
   * segment the api's membership middleware reads — a non-member gets the same
   * 404 a missing space gets, so a caller cannot tell them apart.
   */
  space: (slug: string) => {
    const base = `/t/${encodeURIComponent(slug)}`;
    return {
      /** The space, with the caller's grants and its counts (0084). */
      get: () => client.get<Space>(base),
      update: (body: RequestBody<UpdateTenantRequest>) => client.patch<Tenant>(base, body),
      /** What deleting it would take with it: the owner's to ask. */
      deletion: () => client.get<TenantDeletion>(`${base}/deletion`),
      /**
       * Irreversible. `confirm` must be the space's name, and `otherMembers`
       * how many other people `deletion` said it takes: more in it now, and
       * it is refused (409).
       */
      delete: (body: RequestBody<DeleteTenantRequest>) =>
        client.post<void>(`${base}/deletion`, body),
      /** What leaving could take off its boards: the caller's results shared there. */
      leaving: () => client.get<TenantLeaving>(`${base}/leaving`),
      /**
       * Leaves it, and with `unshare` takes the caller's results off its
       * boards in the same step; each stays in their log. The owner is
       * refused (409): they hand it on first.
       */
      leave: (body: RequestBody<LeaveTenantRequest>) => client.post<void>(`${base}/leaving`, body),
      /**
       * The space's picture (0034), the owner's: a form for a square JPEG,
       * then `media.uploaded`; `remove` takes it down, back to its initials.
       * A personal space has none (400).
       */
      image: {
        start: () => client.post<UploadForm>(`${base}/image`),
        remove: () => client.delete<void>(`${base}/image`),
      },

      /**
       * Pictures (7g). Once an upload form's file is in the store, `uploaded`
       * says so: the api processes it there and then and answers the
       * picture, ready — 202 while it is being processed, 409 when nothing
       * has arrived, 422 when it is not a JPEG within its limits. A profile
       * picture's and a log photo's are confirmed from "me".
       */
      media: {
        uploaded: (mediaId: string) => client.post<Picture>(`${base}/media/${encodeURIComponent(mediaId)}/uploaded`),
      },

      members: {
        /**
         * One page of its members, 50 by default: pass `nextCursor` back for
         * the next, and `q` to find people by name or the start of their
         * username, the same `q` on every page of one search.
         */
        list: (page: { cursor?: string | null; q?: string | null } = {}) => {
          const query = new URLSearchParams();
          if (page.q) query.set("q", page.q);
          if (page.cursor) query.set("cursor", page.cursor);
          return client.get<MemberList>(query.size > 0 ? `${base}/members?${query}` : `${base}/members`);
        },
        update: (userId: string, body: RequestBody<UpdateMemberRequest>) =>
          client.patch<void>(`${base}/members/${encodeURIComponent(userId)}`, body),
        remove: (userId: string) =>
          client.delete<void>(`${base}/members/${encodeURIComponent(userId)}`),
        /**
         * Makes an admin hold exactly these grants. The owner's alone, and only
         * for an admin; `[]` takes every grant away.
         */
        grants: (userId: string, body: UpdateGrantsRequest) =>
          client.put<void>(`${base}/members/${encodeURIComponent(userId)}/grants`, body),
        /**
         * Members whose username starts with `query`, the caller left out:
         * who typing @ suggests. The first few, with an empty query.
         */
        search: (query: string) =>
          client.get<UserList>(`${base}/members/search?q=${encodeURIComponent(query)}`),
      },

      /**
       * One comment, wherever it is: edited by its author, deleted by its
       * author or whoever moderates the space, its replies paged, and
       * reacted to like anything else.
       */
      comments: {
        edit: (id: string, body: EditCommentRequest) =>
          client.patch<SpaceComment>(`${base}/comments/${encodeURIComponent(id)}`, body),
        delete: (id: string) => client.delete<void>(`${base}/comments/${encodeURIComponent(id)}`),
        /** Pass the comment's `repliesCursor` for the replies it did not carry. */
        replies: (id: string, cursor?: string | null) =>
          client.get<CommentPage>(withCursor(`${base}/comments/${encodeURIComponent(id)}/replies`, cursor)),
        react: (id: string, name: string) =>
          client.put<ReactionSummary>(`${base}/comments/${encodeURIComponent(id)}/reaction`, { name }),
        unreact: (id: string) =>
          client.delete<ReactionSummary>(`${base}/comments/${encodeURIComponent(id)}/reaction`),
        reactors: (id: string, page: ReactorQuery = {}) =>
          client.get<ReactorPage>(reactorsPath(`${base}/comments/${encodeURIComponent(id)}`, page)),
      },

      invitations: {
        /** One page of the invitations still waiting, the newest first: pass `nextCursor` back for the next. */
        list: (cursor?: string | null) => client.get<InvitationList>(withCursor(`${base}/invitations`, cursor)),
        create: (body: RequestBody<CreateInvitationRequest>) =>
          client.post<void>(`${base}/invitations`, body),
        // POST rather than DELETE: it carries a body naming who to withdraw,
        // and a DELETE with a body is the kind of thing proxies drop.
        revoke: (body: RequestBody<RevokeInvitationRequest>) =>
          client.post<void>(`${base}/invitations/revoke`, body),
      },

      requests: {
        /** One page of the join queue, the longest waiting first: pass `nextCursor` back for the next. */
        list: (cursor?: string | null) =>
          client.get<MembershipRequestList>(withCursor(`${base}/requests`, cursor)),
        // `role: null` rather than an omitted key: the generator renders an
        // optional Go field as `T | null`, because a nil pointer arrives as an
        // explicit null on the wire. The api reads null as "the default",
        // which for approving somebody is member.
        approve: (id: string, body: ApproveRequestRequest = { role: null }) =>
          client.post<void>(`${base}/requests/${encodeURIComponent(id)}/approve`, body),
        decline: (id: string) =>
          client.post<void>(`${base}/requests/${encodeURIComponent(id)}/decline`),
      },

      /**
       * What the space posts. A member sees what is published; the owner and
       * admins holding manage_events see drafts and scheduled events too, and
       * are the only ones who may write. A draft, to anyone else, is a 404.
       */
      events: {
        /** One page, newest start first. Pass `nextCursor` back for the next. */
        list: (cursor?: string | null) =>
          client.get<EventPage>(
            cursor ? `${base}/events?cursor=${encodeURIComponent(cursor)}` : `${base}/events`,
          ),
        get: (id: string) => client.get<SpaceEvent>(`${base}/events/${encodeURIComponent(id)}`),
        create: (body: RequestBody<CreateEventRequest>) =>
          client.post<SpaceEvent>(`${base}/events`, body),
        /** Changes the fields sent. "" clears what may be cleared. */
        update: (id: string, body: RequestBody<UpdateEventRequest>) =>
          client.patch<SpaceEvent>(`${base}/events/${encodeURIComponent(id)}`, body),
        delete: (id: string) => client.delete<void>(`${base}/events/${encodeURIComponent(id)}`),

        /** An event's thread, and how people reacted to it. */
        conversation: (eventId: string) => thread(client, `${base}/events/${encodeURIComponent(eventId)}`),

        /**
         * Follows a published event (0039), all three on but what the body
         * turns off — or, following it already, changes only the choices
         * sent. Any member; a draft is a 404, or a 409 to its managers.
         */
        follow: (eventId: string, body: RequestBody<FollowEventRequest> = {}) =>
          client.put<EventFollow>(`${base}/events/${encodeURIComponent(eventId)}/follow`, body),
        /** Stops following it, whether or not the caller did. */
        unfollow: (eventId: string) => client.delete<void>(`${base}/events/${encodeURIComponent(eventId)}/follow`),

        /**
         * One athlete's result shared on an event, as the space reads it, and
         * its own thread and reactions.
         */
        results: {
          get: (eventId: string, entryId: string) =>
            client.get<SharedResult>(resultPath(base, eventId, entryId)),
          conversation: (eventId: string, entryId: string) => thread(client, resultPath(base, eventId, entryId)),
          /**
           * Hides the result's photo from this space until it is put back,
           * telling its athlete which space did; the rest of the result stays
           * on the boards (owner, moderate).
           */
          hidePhoto: (eventId: string, entryId: string) =>
            client.put<void>(`${resultPath(base, eventId, entryId)}/photo/hidden`),
          unhidePhoto: (eventId: string, entryId: string) =>
            client.delete<void>(`${resultPath(base, eventId, entryId)}/photo/hidden`),
        },

        /**
         * An event's image, a 16:9 banner (owner, manage_events): a form to
         * post it with, then `media.uploaded`.
         */
        image: {
          start: (eventId: string) => client.post<UploadForm>(`${base}/events/${encodeURIComponent(eventId)}/image`),
          remove: (eventId: string) => client.delete<void>(`${base}/events/${encodeURIComponent(eventId)}/image`),
        },

        /**
         * The caller's own result for an event. `record` registers on the
         * first call — copying the event — and changes the parts sent after
         * that; a result is private until shared.
         */
        entry: {
          get: (eventId: string) => client.get<Entry>(`${base}/events/${encodeURIComponent(eventId)}/entry`),
          record: (eventId: string, body: RequestBody<RecordResultsRequest>) =>
            client.post<Entry>(`${base}/events/${encodeURIComponent(eventId)}/entry`, body),
          share: (eventId: string) =>
            client.put<void>(`${base}/events/${encodeURIComponent(eventId)}/entry/share`),
          /** The result's photo (7g): a form to post it with, then `media.uploaded`. */
          photo: {
            start: (eventId: string) =>
              client.post<UploadForm>(`${base}/events/${encodeURIComponent(eventId)}/entry/photo`),
            remove: (eventId: string) =>
              client.delete<void>(`${base}/events/${encodeURIComponent(eventId)}/entry/photo`),
          },
        },

        /**
         * Takes a result off an event's leaderboards: the caller's own is
         * unshared; anybody else's, for the owner and admins holding
         * moderate, is hidden on every part's board until it is put back.
         * One part at a time is `hide`.
         */
        removeShare: (eventId: string, entryId: string) =>
          client.delete<void>(
            `${base}/events/${encodeURIComponent(eventId)}/shares/${encodeURIComponent(entryId)}`,
          ),

        /**
         * One board of a part: a level (left out when the part has none) and
         * a division. Pass `nextCursor` back for the next page; `limit` is
         * the page's size, the api's 50 when left out.
         */
        leaderboard: (
          eventId: string,
          partId: string,
          board: { level?: string | null; division: string; cursor?: string | null; limit?: number },
        ) => {
          const query = new URLSearchParams({ division: board.division });
          if (board.level) query.set("level", board.level);
          if (board.cursor) query.set("cursor", board.cursor);
          if (board.limit) query.set("limit", String(board.limit));
          return client.get<Leaderboard>(
            `${base}/events/${encodeURIComponent(eventId)}/parts/${encodeURIComponent(partId)}/leaderboard?${query}`,
          );
        },

        /**
         * One board of an event's overall ranking, for an event ranked by sum
         * or by placement points: a level (left out when no counted part has
         * levels) and a division, like a part's.
         */
        overall: (
          eventId: string,
          board: { level?: string | null; division: string; cursor?: string | null; limit?: number },
        ) => {
          const query = new URLSearchParams({ division: board.division });
          if (board.level) query.set("level", board.level);
          if (board.cursor) query.set("cursor", board.cursor);
          if (board.limit) query.set("limit", String(board.limit));
          return client.get<Overall>(`${base}/events/${encodeURIComponent(eventId)}/overall?${query}`);
        },

        /**
         * The results waiting for verification, oldest first, a page at a
         * time, and how many wait in all (owner, verify_scores).
         */
        queue: (eventId: string, page: { cursor?: string | null; limit?: number } = {}) => {
          const query = new URLSearchParams();
          if (page.cursor) query.set("cursor", page.cursor);
          if (page.limit) query.set("limit", String(page.limit));
          const suffix = query.size > 0 ? `?${query}` : "";
          return client.get<VerificationQueue>(`${base}/events/${encodeURIComponent(eventId)}/queue${suffix}`);
        },

        /**
         * Vouches for one athlete's result on one part as it stands, named as
         * a board line or the queue names it; `changedAt` is sent back exactly
         * as it came. Withdrawing takes the verification away.
         */
        verify: (eventId: string, partId: string, entryId: string, body: VerifyRequest) =>
          client.put<void>(verificationPath(base, eventId, partId, entryId), body),
        unverify: (eventId: string, partId: string, entryId: string) =>
          client.delete<void>(verificationPath(base, eventId, partId, entryId)),

        /**
         * Hides one athlete's result on one part's board until it is put
         * back, telling them which space did; `unhide` puts it back (owner,
         * moderate).
         */
        hide: (eventId: string, partId: string, entryId: string) =>
          client.put<void>(hiddenPath(base, eventId, partId, entryId)),
        unhide: (eventId: string, partId: string, entryId: string) =>
          client.delete<void>(hiddenPath(base, eventId, partId, entryId)),
        /**
         * An event's hidden results, the most recently hidden first, a page
         * at a time, and how many there are (owner, moderate).
         */
        hidden: (eventId: string, page: { cursor?: string | null; limit?: number } = {}) => {
          const query = new URLSearchParams();
          if (page.cursor) query.set("cursor", page.cursor);
          if (page.limit) query.set("limit", String(page.limit));
          const suffix = query.size > 0 ? `?${query}` : "";
          return client.get<HiddenResults>(`${base}/events/${encodeURIComponent(eventId)}/hidden${suffix}`);
        },

        /**
         * Teams on an event's team parts (0027). One is formed by `entry.record`
         * with a part's `team`; then its creator changes its score and its
         * people, invitees answer, teammates propose changes, leave and keep
         * their own note, and verifiers and moderators treat it as one result.
         * Each that changes it answers the team as it now stands.
         */
        teams: {
          /** The teams the caller is on, or invited to, on one event. */
          mine: (eventId: string) => client.get<TeamList>(`${base}/events/${encodeURIComponent(eventId)}/teams`),
          get: (eventId: string, teamId: string) => client.get<Team>(teamPath(base, eventId, teamId)),
          /** Its creator's change: the whole result. */
          change: (eventId: string, teamId: string, body: RequestBody<TeamResultRequest>) =>
            client.patch<Team>(teamPath(base, eventId, teamId), body),
          invite: (eventId: string, teamId: string, body: InviteToTeamRequest) =>
            client.post<Team>(`${teamPath(base, eventId, teamId)}/invitations`, body),
          withdrawInvitation: (eventId: string, teamId: string, userId: string) =>
            client.delete<void>(`${teamPath(base, eventId, teamId)}/invitations/${encodeURIComponent(userId)}`),
          addGuests: (eventId: string, teamId: string, body: AddGuestsRequest) =>
            client.post<Team>(`${teamPath(base, eventId, teamId)}/guests`, body),
          changeGuest: (eventId: string, teamId: string, memberId: string, body: GuestRequest) =>
            client.patch<Team>(`${teamPath(base, eventId, teamId)}/guests/${encodeURIComponent(memberId)}`, body),
          removeGuest: (eventId: string, teamId: string, memberId: string) =>
            client.delete<void>(`${teamPath(base, eventId, teamId)}/guests/${encodeURIComponent(memberId)}`),
          /**
           * Joins it. With no result for the event yet, the caller is
           * registered first, privately: ask whether to share it after.
           */
          accept: (eventId: string, teamId: string, body: RequestBody<AcceptTeamRequest> = {}) =>
            client.post<Team>(`${teamPath(base, eventId, teamId)}/accept`, body),
          decline: (eventId: string, teamId: string) =>
            client.post<void>(`${teamPath(base, eventId, teamId)}/decline`),
          leave: (eventId: string, teamId: string) => client.post<void>(`${teamPath(base, eventId, teamId)}/leave`),
          /** A teammate's proposal, one open at a time; the creator answers it by its id. */
          propose: (eventId: string, teamId: string, body: RequestBody<TeamResultRequest>) =>
            client.post<Team>(`${teamPath(base, eventId, teamId)}/proposals`, body),
          withdrawProposal: (eventId: string, teamId: string, proposalId: string) =>
            client.delete<void>(`${teamPath(base, eventId, teamId)}/proposals/${encodeURIComponent(proposalId)}`),
          approve: (eventId: string, teamId: string, proposalId: string) =>
            client.post<Team>(`${teamPath(base, eventId, teamId)}/proposals/${encodeURIComponent(proposalId)}/approve`),
          reject: (eventId: string, teamId: string, proposalId: string) =>
            client.post<Team>(`${teamPath(base, eventId, teamId)}/proposals/${encodeURIComponent(proposalId)}/reject`),
          /** As `verify`, once for the team: `changedAt` exactly as its line gave it. */
          verify: (eventId: string, teamId: string, body: VerifyRequest) =>
            client.put<void>(`${teamPath(base, eventId, teamId)}/verification`, body),
          unverify: (eventId: string, teamId: string) =>
            client.delete<void>(`${teamPath(base, eventId, teamId)}/verification`),
          hide: (eventId: string, teamId: string) => client.put<void>(`${teamPath(base, eventId, teamId)}/hidden`),
          unhide: (eventId: string, teamId: string) =>
            client.delete<void>(`${teamPath(base, eventId, teamId)}/hidden`),
          /** The caller's own note on a team they are on. */
          note: (teamId: string, body: RequestBody<TeamNoteRequest>) =>
            client.put<void>(`${base}/teams/${encodeURIComponent(teamId)}/note`, body),
          /** Its one thread and set of reactions. */
          conversation: (eventId: string, teamId: string) => thread(client, teamPath(base, eventId, teamId)),
        },

        /**
         * A part's title, description and start can change; how it is scored
         * cannot. Each answers with the whole event.
         */
        parts: {
          // Either spelling of the optional fields: the same shape arrives
          // nested in CreateEventRequest, where they are null rather than absent.
          create: (eventId: string, body: RequestBody<CreatePartRequest> | CreatePartRequest) =>
            client.post<SpaceEvent>(`${base}/events/${encodeURIComponent(eventId)}/parts`, body),
          update: (eventId: string, partId: string, body: RequestBody<UpdatePartRequest>) =>
            client.patch<SpaceEvent>(
              `${base}/events/${encodeURIComponent(eventId)}/parts/${encodeURIComponent(partId)}`,
              body,
            ),
          delete: (eventId: string, partId: string) =>
            client.delete<void>(
              `${base}/events/${encodeURIComponent(eventId)}/parts/${encodeURIComponent(partId)}`,
            ),
        },
      },
    };
  },
});

export type Api = ReturnType<typeof createApi>;

/** What the feedback board is filtered by; each left out means every one. */
export type FeedbackFilters = { kind?: string[]; status?: string[] };

function teamPath(base: string, eventId: string, teamId: string): string {
  return `${base}/events/${encodeURIComponent(eventId)}/teams/${encodeURIComponent(teamId)}`;
}

function resultPath(base: string, eventId: string, entryId: string): string {
  return `${base}/events/${encodeURIComponent(eventId)}/shares/${encodeURIComponent(entryId)}`;
}

function withCursor(path: string, cursor?: string | null): string {
  return cursor ? `${path}?cursor=${encodeURIComponent(cursor)}` : path;
}

/** Which of who reacted: with one reaction, or any; and where the last page ended. */
type ReactorQuery = { name?: string | null; cursor?: string | null };

function reactorsPath(path: string, page: ReactorQuery): string {
  const query = new URLSearchParams();
  if (page.name) query.set("name", page.name);
  if (page.cursor) query.set("cursor", page.cursor);
  return query.size > 0 ? `${path}/reactions?${query}` : `${path}/reactions`;
}

/** A thread — an event's, a result's or a team's — at `path`. */
function thread(client: ApiClient, path: string) {
  return {
    /** One page, oldest first; each top-level comment carries its first replies. */
    comments: (cursor?: string | null) => client.get<CommentPage>(withCursor(`${path}/comments`, cursor)),
    /** A comment, or with `replyTo` a reply. An `id` of your own makes a retry safe. */
    comment: (body: RequestBody<WriteCommentRequest>) => client.post<SpaceComment>(`${path}/comments`, body),
    /**
     * Gives or changes the caller's reaction, or takes it back; each answers
     * how people reacted, as it now stands.
     */
    react: (name: string) => client.put<ReactionSummary>(`${path}/reaction`, { name }),
    unreact: () => client.delete<ReactionSummary>(`${path}/reaction`),
    /** Who reacted, oldest first: with one reaction, or any. */
    reactors: (page: ReactorQuery = {}) => client.get<ReactorPage>(reactorsPath(path, page)),
  };
}

function hiddenPath(base: string, eventId: string, partId: string, entryId: string): string {
  return `${base}/events/${encodeURIComponent(eventId)}/parts/${encodeURIComponent(partId)}/hidden/${encodeURIComponent(entryId)}`;
}

function verificationPath(base: string, eventId: string, partId: string, entryId: string): string {
  return `${base}/events/${encodeURIComponent(eventId)}/parts/${encodeURIComponent(partId)}/verifications/${encodeURIComponent(entryId)}`;
}
